import { computed, defineComponent, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  Cell,
  Icon as VanIcon,
  NavBar as VanNavBar,
  Search as VanSearch,
  showFailToast,
  showSuccessToast,
} from 'vant'
import { useAuthGuard } from '@/composables/use-auth-guard'
import { useInfiniteScroll } from '@/composables/use-infinite-scroll'
import { useUserStore } from '@/stores/user-store'
import { getApiV1UserFollowings, getApiV1UserFollowList } from '@/api/sdk.gen'
import type { FanItem } from '@/api/types.gen'
import emptyImg from '@/assets/empty-data.png'
import UserAvatar from '@/components/user-avatar'

export default defineComponent({
  name: 'MyFollowsPage',
  setup() {
    const route = useRoute()
  const goBack = useSmartBack()
    const userStore = useUserStore()
    useAuthGuard()

    // ?userId= 存在则查指定用户的关注列表（"ta 的关注"）；不传则走旧的"我的关注"接口
    const targetUserId = computed(() => Number(route.query.userId) || 0)
    const isOther = computed(() => targetUserId.value > 0)
    const navTitle = computed(() => (isOther.value ? 'ta的关注' : '我的关注'))

    const keyword = ref('')
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const { items, isEmpty, load, reset } = useInfiniteScroll<FanItem>(async (page, size) => {
      const res = isOther.value
        ? await getApiV1UserFollowings({
            query: { page, size, user_id: targetUserId.value },
          })
        : await getApiV1UserFollowList({
            query: { page, size, nickname: keyword.value || undefined },
          })
      const data = res.data as { code: number; data: { list: FanItem[]; total: number } }
      return data.code === 0 ? data.data : { list: [], total: 0 }
    })

    load()

    watch(keyword, () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => reset(), 300)
    })

    async function handleUnfollow(e: Event, userId: number) {
      e.stopPropagation()
      try {
        const result = await userStore.toggleFollow(userId)
        if (result.code === 0) {
          items.value = items.value.filter((u) => u.user_id !== userId)
          showSuccessToast('已取消关注')
        } else {
          showFailToast(result.message || '操作失败')
        }
      } catch {
        showFailToast('操作失败')
      }
    }

    /** 关注列表的一行 */
    const renderFollow = (user: FanItem) => (
      <Cell key={user.id} class="list-item">
        {{
          icon: () => (
            <RouterLink class="btn-reset" to={`/user/${user.user_id}`}>
              <UserAvatar size="0.84rem" src={user.avatar} />
            </RouterLink>
          ),
          title: () => <div>{user.nickname}</div>,
          'right-icon': () =>
            isOther.value ? null : (
              <button
                type="button"
                class="cancel-care btn-reset"
                onClick={(e: Event) => handleUnfollow(e, user.user_id!)}
              >
                <span>取消关注</span>
              </button>
            ),
        }}
      </Cell>
    )

    return () => (
      <div class="mycare-wrap">
        <VanNavBar title={navTitle.value} fixed>
          {{
            left: () => (
              <VanIcon name="arrow-left" color="#333" size="16" onClick={() => goBack()} />
            ),
          }}
        </VanNavBar>

        <div class="search-cj">
          <VanSearch
            v-model={keyword.value}
            showAction
            placeholder="查找我关注的作者名"
            onSearch={() => reset()}
          >
            {{
              action: () => (
                <button type="button" class="btn-reset" onClick={() => reset()}>
                  搜索
                </button>
              ),
            }}
          </VanSearch>
        </div>

        <div class="van-pull-refresh">
          {items.value.map(renderFollow)}

          {isEmpty.value && (
            <div class="text-center pt-[0.4rem] pb-[0.4rem]">
              <img src={emptyImg} class="w-[1.28rem]" alt="" />
              <p class="text-center text-[#a0a0a0] text-[0.32rem] leading-[0.44rem] mt-[0.28rem]">
                暂无关注
              </p>
            </div>
          )}
        </div>
      </div>
    )
  },
})
